import React from 'react';
import styled from 'styled-components';
import { Text } from './Typography';
import { TableContainer } from './Containers';
import PropTypes from 'prop-types';

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border}30;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${({ theme, variant, status }) => {
    if (variant === 'header') {
      return '';
    }
    
    const backgrounds = {
      pending: `${theme.colors.warning}08`,
      accepted: theme.colors.background,
      rejected: `${theme.colors.error}08`,
      success: `${theme.colors.success}08`,
      warning: `${theme.colors.warning}08`,
      error: `${theme.colors.error}08`,
      default: theme.colors.background
    };
    
    return `background-color: ${backgrounds[status] || backgrounds.default};`;
  }}

  &:nth-child(even) {
    ${({ theme, variant, status }) => {
      if (variant === 'header') {
        return '';
      }
      
      const backgrounds = {
        pending: `${theme.colors.warning}12`,
        accepted: theme.colors.backgroundSecondary || theme.colors.background,
        rejected: `${theme.colors.error}12`,
        success: `${theme.colors.success}12`,
        warning: `${theme.colors.warning}12`,
        error: `${theme.colors.error}12`,
        default: theme.colors.backgroundSecondary || theme.colors.background
      };
      
      return `background-color: ${backgrounds[status] || backgrounds.default};`;
    }}
  }

  &:hover {
    ${({ theme, variant, status, hoverable = true }) => {
      if (variant === 'header' || !hoverable) {
        return '';
      }
      
      const backgrounds = {
        pending: `${theme.colors.warning}15`,
        accepted: theme.colors.primaryLight || `${theme.colors.primary}08`,
        rejected: `${theme.colors.error}15`,
        success: `${theme.colors.success}15`,
        warning: `${theme.colors.warning}15`,
        error: `${theme.colors.error}15`,
        default: theme.colors.primaryLight || `${theme.colors.primary}08`
      };
      
      return `
        background-color: ${backgrounds[status] || backgrounds.default};
        transform: translateY(-1px);
        box-shadow: ${theme.shadows.small};
      `;
    }}
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse}; 
  vertical-align: middle;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.text.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border}20;
  text-align: ${({ align }) => align || 'left'};
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  .icon {
    font-size: 48px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
    opacity: 0.5;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.background}80;
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const Table = ({
  columns = [],
  data = [],
  renderCell,
  onRowClick,
  isLoading = false,
  error = null,
  emptyState = null,
  className,
  getRowStatus,
  hoverable = true,
  ...props
}) => {
  // Default empty state renderer
  const renderEmptyState = (icon, title, subtitle) => (
    <EmptyState>
      {icon && React.createElement(icon, { className: 'icon' })}
      <Text size="lg" weight="medium" style={{ margin: '0 0 8px 0' }}>
        {title}
      </Text>
      {subtitle && (
        <Text size="sm" style={{ margin: 0, opacity: 0.7 }}>
          {subtitle}
        </Text>
      )}
    </EmptyState>
  );

  // Default cell renderer
  const defaultRenderCell = (item, column) => {
    const value = item[column.key];
    return value !== null && value !== undefined ? String(value) : 'N/A';
  };

  const cellRenderer = renderCell || defaultRenderCell;

  // Error state
  if (error) {
    return (
      <TableContainer className={className}>
        {emptyState ? emptyState : renderEmptyState(
          null, 
          'Error Loading Data', 
          typeof error === 'string' ? error : 'An error occurred while loading data'
        )}
      </TableContainer>
    );
  }

  return (
    <TableContainer className={className} style={{ position: 'relative' }} {...props}>
      {isLoading && (
        <LoadingOverlay>
          <Text>Loading data...</Text>
        </LoadingOverlay>
      )}
      
      <StyledTable>
        <thead>
          <TableRow variant="header">
            {columns.map(({ key, label, icon: Icon, align, width, ...columnProps }) => (
              <TableHeader 
                key={key} 
                align={align}
                style={{ width }}
                {...columnProps}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {Icon && <Icon size={16} />}
                  {label}
                </div>
              </TableHeader>
            ))}
          </TableRow>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, index) => {
              const rowStatus = getRowStatus ? getRowStatus(item, index) : 'default';
              
              return (
                <TableRow 
                  key={item.id || index}
                  status={rowStatus}
                  hoverable={hoverable}
                  onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                > 
                  {columns.map((column) => (
                    <TableCell 
                      key={`${item.id || index}-${column.key}`}
                      align={column.align}
                    >
                      {cellRenderer(item, column, index)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>
                {emptyState ? emptyState : renderEmptyState(
                  null,
                  'No Data Available',
                  'No records found to display.'
                )}
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      width: PropTypes.string
    })
  ).isRequired,
  data: PropTypes.array,
  renderCell: PropTypes.func,
  onRowClick: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  emptyState: PropTypes.node,
  className: PropTypes.string,
  getRowStatus: PropTypes.func,
  hoverable: PropTypes.bool
};

export default React.memo(Table);